import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, UserCheck, Mail, Settings2, ShoppingBag, Truck,
  Plus, Edit2, Check, X, RefreshCw, Copy,
  Eye, EyeOff, Wifi, ChevronRight, ImageIcon,
  MapPin, Package, ToggleLeft, ToggleRight, AlertCircle,
} from 'lucide-react';
import { adminSettings, adminCollections, adminUploads,
         adminPickupLocations, adminShippingRules } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

// ── Toast ──────────────────────────────────────────────────────────────────────

function ToastContainer({ toasts, remove }) {
  return (
    <div
      style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, minWidth: 280 }}
      aria-live="polite"
    >
      {toasts.map(t => (
        <div key={t.id} className={`toast show mb-2 border-0 shadow-sm text-white bg-${t.type === 'error' ? 'danger' : 'success'}`}>
          <div className="d-flex align-items-center px-3 py-2">
            <span className="me-auto">{t.message}</span>
            <button className="btn-close btn-close-white ms-2" onClick={() => remove(t.id)} />
          </div>
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(ts => [...ts, { id, message, type }]);
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 4000);
  }, []);
  const remove = useCallback(id => setToasts(ts => ts.filter(t => t.id !== id)), []);
  return { toasts, add, remove };
}

// ── Confirm Modal ──────────────────────────────────────────────────────────────

function ConfirmModal({ show, title, body, onConfirm, onCancel, danger = true }) {
  if (!show) return null;
  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onCancel} />
          </div>
          <div className="modal-body">{body}</div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className={`btn btn-${danger ? 'danger' : 'primary'}`} onClick={onConfirm}>Confirm</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab 1: Users & Access ──────────────────────────────────────────────────────

function UsersTab({ toast }) {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [editRow, setEditRow]     = useState(null); // user id being edited
  const [editData, setEditData]   = useState({});
  const [confirm, setConfirm]     = useState(null); // { userId, action }
  const [tempPwd, setTempPwd]     = useState(null); // { userId, name, password }
  const [saving, setSaving]       = useState(false);
  const [addForm, setAddForm]     = useState({ firstName:'', lastName:'', email:'', role:'sales_rep', password:'', phone:'' });

  const load = useCallback(() => {
    setLoading(true);
    adminSettings.getUsers()
      .then(r => setUsers(r.data.users || []))
      .catch(() => toast('Failed to load users.', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (u) => {
    setEditRow(u.id);
    setEditData({ firstName: u.firstName, lastName: u.lastName, email: u.email, role: u.role, phone: u.phone || '' });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await adminSettings.updateUser(editRow, editData);
      toast('User updated.');
      setEditRow(null);
      load();
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to update user.', 'error');
    } finally { setSaving(false); }
  };

  const handleDeactivate = async () => {
    const { userId } = confirm;
    setConfirm(null);
    try {
      const r = await adminSettings.deactivateUser(userId);
      toast(r.data.isActive ? 'User reactivated.' : 'User deactivated.');
      load();
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to toggle user.', 'error');
    }
  };

  const handleResetPassword = async (userId, name) => {
    try {
      const r = await adminSettings.resetPassword(userId);
      setTempPwd({ userId, name, password: r.data.tempPassword });
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to reset password.', 'error');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminSettings.createUser(addForm);
      toast('User created.');
      setShowAdd(false);
      setAddForm({ firstName:'', lastName:'', email:'', role:'sales_rep', password:'', phone:'' });
      load();
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to create user.', 'error');
    } finally { setSaving(false); }
  };

  const roleBadge = (role) =>
    role === 'admin'
      ? <span className="badge bg-dark">Admin</span>
      : <span className="badge bg-secondary">Sales Rep</span>;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-semibold">Admin &amp; Sales Rep Users</h6>
        <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={() => setShowAdd(true)}>
          <Plus size={14} /> Add User
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : users.length === 0 ? (
        <div className="text-center text-muted py-5">
          <Users size={36} className="mb-2 opacity-50" /><p>No users found.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th><th>Email</th><th>Phone</th><th>Role</th>
                <th>Status</th><th>Last Login</th><th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  {editRow === u.id ? (
                    <>
                      <td>
                        <div className="d-flex gap-1">
                          <input className="form-control form-control-sm" style={{width:100}} value={editData.firstName}
                            onChange={e => setEditData(d=>({...d, firstName:e.target.value}))} placeholder="First" />
                          <input className="form-control form-control-sm" style={{width:100}} value={editData.lastName}
                            onChange={e => setEditData(d=>({...d, lastName:e.target.value}))} placeholder="Last" />
                        </div>
                      </td>
                      <td>
                        <input className="form-control form-control-sm" value={editData.email}
                          onChange={e => setEditData(d=>({...d, email:e.target.value}))} />
                      </td>
                      <td>
                        <input className="form-control form-control-sm" value={editData.phone}
                          onChange={e => setEditData(d=>({...d, phone:e.target.value}))} placeholder="Phone" />
                      </td>
                      <td>
                        <select className="form-select form-select-sm" value={editData.role}
                          onChange={e => setEditData(d=>({...d, role:e.target.value}))}>
                          <option value="admin">Admin</option>
                          <option value="sales_rep">Sales Rep</option>
                        </select>
                      </td>
                      <td colSpan={2}></td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-success btn-sm" onClick={saveEdit} disabled={saving}>
                            {saving ? <span className="spinner-border spinner-border-sm" /> : <Check size={12} />}
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditRow(null)}><X size={12} /></button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="fw-semibold">{u.firstName} {u.lastName}</td>
                      <td>{u.email}</td>
                      <td>{u.phone || '—'}</td>
                      <td>{roleBadge(u.role)}</td>
                      <td>
                        <span className={`badge ${u.isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-muted small">
                        {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString('en-US') : '—'}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-outline-primary btn-sm" title="Edit" onClick={() => startEdit(u)}>
                            <Edit2 size={12} />
                          </button>
                          <button
                            className={`btn btn-sm ${u.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            title={u.isActive ? 'Deactivate' : 'Reactivate'}
                            onClick={() => setConfirm({ userId: u.id, action: u.isActive ? 'deactivate' : 'reactivate', name: `${u.firstName} ${u.lastName}` })}
                          >
                            <UserCheck size={12} />
                          </button>
                          <button className="btn btn-outline-secondary btn-sm" title="Reset Password"
                            onClick={() => handleResetPassword(u.id, `${u.firstName} ${u.lastName}`)}>
                            <RefreshCw size={12} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add User Modal */}
      {showAdd && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add User</h5>
                <button className="btn-close" onClick={() => setShowAdd(false)} />
              </div>
              <form onSubmit={handleAdd}>
                <div className="modal-body">
                  <div className="row g-2 mb-2">
                    <div className="col">
                      <label className="form-label">First Name</label>
                      <input className="form-control" required value={addForm.firstName}
                        onChange={e => setAddForm(f=>({...f,firstName:e.target.value}))} />
                    </div>
                    <div className="col">
                      <label className="form-label">Last Name</label>
                      <input className="form-control" required value={addForm.lastName}
                        onChange={e => setAddForm(f=>({...f,lastName:e.target.value}))} />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" required value={addForm.email}
                      onChange={e => setAddForm(f=>({...f,email:e.target.value}))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={addForm.phone}
                      onChange={e => setAddForm(f=>({...f,phone:e.target.value}))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={addForm.role}
                      onChange={e => setAddForm(f=>({...f,role:e.target.value}))}>
                      <option value="admin">Admin</option>
                      <option value="sales_rep">Sales Rep</option>
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Password <small className="text-muted">(min 8 chars)</small></label>
                    <input type="password" className="form-control" required minLength={8} value={addForm.password}
                      onChange={e => setAddForm(f=>({...f,password:e.target.value}))} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? <span className="spinner-border spinner-border-sm" /> : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Temp Password Modal */}
      {tempPwd && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Temporary Password — {tempPwd.name}</h5>
                <button className="btn-close" onClick={() => setTempPwd(null)} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-2">
                  Deliver this password to the user manually. They should change it on next login.
                </p>
                <div className="d-flex align-items-center gap-2">
                  <code className="fs-5 flex-grow-1 bg-light px-3 py-2 rounded">{tempPwd.password}</code>
                  <button className="btn btn-outline-secondary btn-sm"
                    onClick={() => { navigator.clipboard.writeText(tempPwd.password); toast('Copied!'); }}>
                    <Copy size={14} />
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => setTempPwd(null)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!confirm}
        title={confirm?.action === 'deactivate' ? 'Deactivate User' : 'Reactivate User'}
        body={`Are you sure you want to ${confirm?.action} ${confirm?.name}?`}
        danger={confirm?.action === 'deactivate'}
        onConfirm={handleDeactivate}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}

// ── Tab 2: Sales Reps ──────────────────────────────────────────────────────────

function RepsTab({ toast }) {
  const [reps, setReps]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editPhone, setEditPhone] = useState({}); // { [repId]: value }
  const [saving,    setSaving]    = useState(null);
  const [editRate,  setEditRate]  = useState({}); // { [repId]: value }
  const [savingRate, setSavingRate] = useState(null);

  const load = useCallback(() => {
    adminSettings.getReps()
      .then(r => setReps(r.data.reps || []))
      .catch(() => toast('Failed to load reps.', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const savePhone = async (rep) => {
    setSaving(rep.id);
    try {
      await adminSettings.updateRepPhone(rep.id, editPhone[rep.id] ?? rep.phone ?? '');
      toast('Phone updated.');
      setEditPhone(p => { const n={...p}; delete n[rep.id]; return n; });
      load();
    } catch {
      toast('Failed to update phone.', 'error');
    } finally { setSaving(null); }
  };

  const saveRate = async (rep) => {
    setSavingRate(rep.id);
    try {
      await adminSettings.updateRepCommissionRate(rep.id, editRate[rep.id] ?? rep.commissionRate ?? 0);
      toast('Commission rate updated.');
      setEditRate(r => { const n={...r}; delete n[rep.id]; return n; });
      load();
    } catch {
      toast('Failed to update commission rate.', 'error');
    } finally { setSavingRate(null); }
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;

  if (reps.length === 0) {
    return (
      <div className="text-center text-muted py-5">
        <UserCheck size={36} className="mb-2 opacity-50" />
        <p>No sales reps found.</p>
        <p className="small">Create a user with the "Sales Rep" role in the Users &amp; Access tab.</p>
      </div>
    );
  }

  return (
    <>
      <h6 className="fw-semibold mb-3">Sales Representatives</h6>
      <div className="row g-3">
        {reps.map(rep => (
          <div className="col-md-6 col-xl-4" key={rep.id}>
            <div className={`card h-100 shadow-sm ${!rep.isActive ? 'opacity-50' : ''}`}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-1">
                  <h6 className="mb-0 fw-semibold">{rep.firstName} {rep.lastName}</h6>
                  <span className="badge bg-primary rounded-pill">{rep.assignedCount} customers</span>
                </div>
                <p className="text-muted small mb-2">{rep.email}</p>

                {/* Phone inline edit */}
                {rep.id in editPhone ? (
                  <div className="d-flex gap-1 align-items-center mb-2">
                    <input className="form-control form-control-sm" value={editPhone[rep.id]}
                      onChange={e => setEditPhone(p=>({...p,[rep.id]:e.target.value}))}
                      placeholder="Phone number" />
                    <button className="btn btn-success btn-sm" onClick={() => savePhone(rep)} disabled={saving===rep.id}>
                      {saving===rep.id ? <span className="spinner-border spinner-border-sm" /> : <Check size={12} />}
                    </button>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => setEditPhone(p=>{ const n={...p}; delete n[rep.id]; return n; })}>
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="text-muted small">{rep.phone || 'No phone'}</span>
                    <button className="btn btn-link btn-sm p-0 text-muted"
                      onClick={() => setEditPhone(p=>({...p,[rep.id]:rep.phone||''}))}>
                      <Edit2 size={12} />
                    </button>
                  </div>
                )}

                {/* Commission Rate inline edit */}
                {rep.id in editRate ? (
                  <div className="d-flex gap-1 align-items-center mb-2">
                    <div className="input-group input-group-sm" style={{ maxWidth: 140 }}>
                      <input
                        type="number" className="form-control form-control-sm"
                        step="0.01" min="0" max="100"
                        value={editRate[rep.id]}
                        onChange={e => setEditRate(r => ({ ...r, [rep.id]: e.target.value }))}
                        placeholder="0.00"
                      />
                      <span className="input-group-text">%</span>
                    </div>
                    <button className="btn btn-success btn-sm" onClick={() => saveRate(rep)} disabled={savingRate===rep.id}>
                      {savingRate===rep.id ? <span className="spinner-border spinner-border-sm" /> : <Check size={12} />}
                    </button>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => setEditRate(r => { const n={...r}; delete n[rep.id]; return n; })}>
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    {parseFloat(rep.commissionRate || 0) === 0 ? (
                      <span className="text-muted small fst-italic">No commission set</span>
                    ) : (
                      <span className="fw-semibold text-dark small">
                        {parseFloat(rep.commissionRate).toFixed(2)}% commission
                      </span>
                    )}
                    <button className="btn btn-link btn-sm p-0 text-muted"
                      onClick={() => setEditRate(r => ({ ...r, [rep.id]: parseFloat(rep.commissionRate || 0).toFixed(2) }))}>
                      <Edit2 size={12} />
                    </button>
                  </div>
                )}

                <a
                  href={`/admin/customers?rep=${rep.id}`}
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                >
                  View Customers <ChevronRight size={12} />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Tab 3: Email & Notifications ───────────────────────────────────────────────

function ChipInput({ value, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const chips = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];

  const add = () => {
    const v = input.trim();
    if (!v) return;
    const next = [...new Set([...chips, v])].join(',');
    onChange(next);
    setInput('');
  };

  const remove = (chip) => {
    onChange(chips.filter(c => c !== chip).join(','));
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {chips.map(c => (
          <span key={c} className="badge bg-primary d-inline-flex align-items-center gap-1">
            {c} days
            <button className="btn-close btn-close-white ms-1" style={{fontSize:8}} onClick={() => remove(c)} />
          </span>
        ))}
      </div>
      <div className="input-group input-group-sm" style={{maxWidth:220}}>
        <input type="number" className="form-control" placeholder={placeholder || 'Days'}
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key==='Enter') { e.preventDefault(); add(); }}} />
        <button className="btn btn-outline-secondary" type="button" onClick={add}>Add</button>
      </div>
    </div>
  );
}

function EmailTab({ toast, settings, onSettingsChange }) {
  const [reveal, setReveal]                   = useState(false);
  const [apiKey, setApiKey]                   = useState('');
  const [testing, setTesting]                 = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [testEmailAddr, setTestEmailAddr]     = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [instrSaving, setInstrSaving]         = useState(false);

  useEffect(() => {
    setApiKey(settings.resend_api_key || '');
  }, [settings.resend_api_key]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSettings.updateSystem({
        resend_api_key:              apiKey,
        invoice_email_from_name:     settings.invoice_email_from_name     || '',
        invoice_email_from_address:  settings.invoice_email_from_address  || '',
        invoice_reminder_days:       settings.invoice_reminder_days       || '',
        overdue_reminder_days:       settings.overdue_reminder_days       || '',
        admin_notification_email:    settings.admin_notification_email    || '',
        portal_url:                  settings.portal_url                  || '',
      });
      toast('Email settings saved.');
    } catch {
      toast('Failed to save settings.', 'error');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await adminSettings.testResend({});
      toast(r.data.message || 'Connection successful.');
    } catch (e) {
      toast(e.response?.data?.message || 'Connection failed.', 'error');
    } finally { setTesting(false); }
  };

  const handleSendTest = async () => {
    if (!testEmailAddr.trim()) return;
    setTestEmailSending(true);
    try {
      const r = await adminSettings.testResend({ testEmail: testEmailAddr.trim() });
      toast(r.data.message || `Test email sent to ${testEmailAddr}.`);
      setTestEmailAddr('');
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to send test email.', 'error');
    } finally { setTestEmailSending(false); }
  };

  const handleInstrBlur = async () => {
    setInstrSaving(true);
    try {
      await adminSettings.updateSystem({ payment_instructions: settings.payment_instructions || '' });
      toast('Payment instructions saved.');
    } catch {
      toast('Failed to save payment instructions.', 'error');
    } finally { setInstrSaving(false); }
  };

  const instrValue = settings.payment_instructions || '';

  return (
    <>
      <h6 className="fw-semibold mb-3">Resend &amp; Email Notifications</h6>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Resend API</div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Resend API Key</label>
            <div className="input-group">
              <input
                type={reveal ? 'text' : 'password'}
                className="form-control"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="re_..."
              />
              <button className="btn btn-outline-secondary" type="button" onClick={() => setReveal(r => !r)}>
                {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="form-text">
              Get your API key from{' '}
              <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer">
                resend.com/api-keys
              </a>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3 flex-wrap">
            <button
              className="btn btn-outline-info btn-sm d-inline-flex align-items-center gap-1"
              onClick={handleTest}
              disabled={testing || testEmailSending}
            >
              {testing ? <span className="spinner-border spinner-border-sm" /> : <Wifi size={13} />}
              Test Connection
            </button>
          </div>

          {/* Send Test Email */}
          <div className="mt-3 pt-3 border-top">
            <label className="form-label fw-semibold small mb-1">Send Test Email</label>
            <div className="input-group input-group-sm" style={{ maxWidth: 420 }}>
              <input
                type="email"
                className="form-control"
                placeholder="recipient@example.com"
                value={testEmailAddr}
                onChange={e => setTestEmailAddr(e.target.value)}
              />
              <button
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
                onClick={handleSendTest}
                disabled={testEmailSending || !testEmailAddr.trim()}
              >
                {testEmailSending
                  ? <span className="spinner-border spinner-border-sm" />
                  : <Mail size={13} />}
                Send Test
              </button>
            </div>
            <p className="text-muted small mt-1 mb-0">
              Sends a test message using the current API key and From address.
            </p>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">From Address</div>
        <div className="card-body row g-3">
          <div className="col-md-6">
            <label className="form-label">From Name</label>
            <input
              className="form-control"
              value={settings.invoice_email_from_name || ''}
              onChange={e => onSettingsChange('invoice_email_from_name', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">From Email</label>
            <input
              type="email"
              className="form-control"
              value={settings.invoice_email_from_address || ''}
              onChange={e => onSettingsChange('invoice_email_from_address', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Invoice Payment Instructions</div>
        <div className="card-body">
          <label className="form-label">
            Invoice Payment Instructions
            <small className="text-muted ms-2">({instrValue.length}/1000)</small>
          </label>
          <textarea
            className="form-control"
            rows={5}
            maxLength={1000}
            value={instrValue}
            onChange={e => onSettingsChange('payment_instructions', e.target.value)}
            onBlur={handleInstrBlur}
            placeholder="e.g. Make checks payable to Jeremy Wine Co.&#10;Mail to: 123 Vineyard Rd, Napa CA 94558&#10;Wire transfer: Bank of Napa, Routing 123456789, Acct 987654321"
          />
          <div className="form-text d-flex align-items-center gap-2">
            Shown on all invoice emails — include check payable to, mailing address, wire
            transfer details, etc.
            {instrSaving && <span className="spinner-border spinner-border-sm text-secondary" />}
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Notifications &amp; Portal</div>
        <div className="card-body row g-3">
          <div className="col-md-6">
            <label className="form-label">Admin Notification Email</label>
            <input
              type="email"
              className="form-control"
              value={settings.admin_notification_email || ''}
              onChange={e => onSettingsChange('admin_notification_email', e.target.value)}
              placeholder="admin@example.com"
            />
            <div className="form-text">Receives an email when a new wholesale customer registers.</div>
          </div>
          <div className="col-md-6">
            <label className="form-label">Portal Base URL</label>
            <input
              type="url"
              className="form-control"
              value={settings.portal_url || ''}
              onChange={e => onSettingsChange('portal_url', e.target.value)}
              placeholder="https://wholesale.jeremywineco.com"
            />
            <div className="form-text">Used in email CTA links (e.g. "Review Application").</div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Reminder Schedules</div>
        <div className="card-body">
          <div className="mb-4">
            <label className="form-label fw-semibold">Invoice Reminders — days <em>before</em> due date</label>
            <ChipInput
              value={settings.invoice_reminder_days || ''}
              onChange={v => onSettingsChange('invoice_reminder_days', v)}
            />
          </div>
          <div>
            <label className="form-label fw-semibold">Overdue Reminders — days <em>after</em> due date</label>
            <ChipInput
              value={settings.overdue_reminder_days || ''}
              onChange={v => onSettingsChange('overdue_reminder_days', v)}
            />
          </div>
          <p className="text-muted small mt-3 mb-0">
            Reminder emails will be sent automatically based on this schedule.
            Manual send is available on each order detail page.
          </p>
        </div>
      </div>

      <button
        className="btn btn-primary d-inline-flex align-items-center gap-1"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? <span className="spinner-border spinner-border-sm" /> : null}
        Save Email Settings
      </button>
    </>
  );
}

// ── Tab 4: Portal Settings ─────────────────────────────────────────────────────

function PortalTab({ toast, settings, onSettingsChange }) {
  const [saving, setSaving] = useState(false);
  const announcement = settings.portal_announcement || '';

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSettings.updateSystem({
        portal_announcement: settings.portal_announcement || '',
        default_net_days:    settings.default_net_days    || '30',
        default_credit_cap:  settings.default_credit_cap  || '0',
        min_order_enabled:   settings.min_order_enabled   ?? '1',
        min_order_bottles:   settings.min_order_bottles   || '12',
      });
      toast('Portal settings saved.');
    } catch {
      toast('Failed to save settings.', 'error');
    } finally { setSaving(false); }
  };

  return (
    <>
      <h6 className="fw-semibold mb-3">Portal Settings</h6>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Portal Announcement</div>
        <div className="card-body">
          <label className="form-label">
            Announcement Banner
            <small className="text-muted ms-2">
              ({announcement.length}/500) — shown on the customer dashboard when non-empty
            </small>
          </label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={500}
            value={announcement}
            onChange={e => onSettingsChange('portal_announcement', e.target.value)}
            placeholder="e.g. Our offices will be closed Dec 25–Jan 1."
          />
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Defaults for New Customers</div>
        <div className="card-body row g-3">
          <div className="col-md-4">
            <label className="form-label">Default Net Days</label>
            <div className="input-group">
              <input type="number" min={0} className="form-control"
                value={settings.default_net_days || ''}
                onChange={e => onSettingsChange('default_net_days', e.target.value)} />
              <span className="input-group-text">days</span>
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label">Default Credit Cap</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input type="number" min={0} step="0.01" className="form-control"
                value={settings.default_credit_cap || ''}
                onChange={e => onSettingsChange('default_credit_cap', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Minimum Order</div>
        <div className="card-body">
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="minOrderEnabled"
              checked={settings.min_order_enabled !== '0'}
              onChange={e => onSettingsChange('min_order_enabled', e.target.checked ? '1' : '0')}
            />
            <label className="form-check-label" htmlFor="minOrderEnabled">
              Enforce minimum order quantity at checkout
            </label>
          </div>
          {settings.min_order_enabled !== '0' && (
            <div className="col-md-4">
              <label className="form-label">Minimum bottles per order</label>
              <div className="input-group">
                <input
                  type="number"
                  min={1}
                  className="form-control"
                  value={settings.min_order_bottles || '12'}
                  onChange={e => onSettingsChange('min_order_bottles', e.target.value)}
                />
                <span className="input-group-text">bottles</span>
              </div>
              <div className="form-text">12 bottles = 1 standard case.</div>
            </div>
          )}
        </div>
      </div>

      <button className="btn btn-primary d-inline-flex align-items-center gap-1" onClick={handleSave} disabled={saving}>
        {saving ? <span className="spinner-border spinner-border-sm" /> : null}
        Save Portal Settings
      </button>
    </>
  );
}

// ── CollectionModal ────────────────────────────────────────────────────────────
// Props:
//   collection  — null (new) | collection object (edit)
//   onSaved(action)  — called with 'created' | 'updated' | 'deleted'
//   onClose()
//   toast(msg, type)

const WINE_TYPES = ['Red', 'White', 'Rosé', 'Sparkling', 'Dessert'];

function CollectionModal({ collection, onSaved, onClose, toast }) {
  const isEdit = !!collection;

  const [form, setForm] = useState({
    name:         collection?.name        || '',
    description:  collection?.description || '',
    coverImageUrl: collection?.coverImageUrl || '',
    wineType:  '',
    varietal:  '',
    vintage:   '',
    maxPrice:  '',
    isActive:  collection ? !!collection.isActive : true,
  });

  // Parse filterJson into individual fields on mount
  useEffect(() => {
    if (!collection?.filterJson) return;
    try {
      const f = JSON.parse(collection.filterJson);
      setForm(prev => ({
        ...prev,
        wineType: f.wineType  || '',
        varietal: f.varietal  || '',
        vintage:  f.vintage   ? String(f.vintage)  : '',
        maxPrice: f.maxPrice  ? String(f.maxPrice)  : '',
      }));
    } catch { /* malformed JSON — ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [saving,    setSaving]    = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const coverFileRef = useRef(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Build filterJson from individual form fields
  const buildFilterJson = () => {
    const f = {};
    if (form.wineType)         f.wineType = form.wineType;
    if (form.varietal.trim())  f.varietal = form.varietal.trim();
    if (form.vintage)          f.vintage  = parseInt(form.vintage, 10);
    if (form.maxPrice)         f.maxPrice = parseFloat(form.maxPrice);
    return Object.keys(f).length ? JSON.stringify(f) : null;
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    setImgUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await adminUploads.uploadCollectionImage(fd);
      if (res.data?.success) set('coverImageUrl', res.data.url);
    } catch {
      toast('Failed to upload image.', 'error');
    } finally {
      setImgUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      name:         form.name.trim(),
      description:  form.description.trim() || null,
      coverImageUrl: form.coverImageUrl || null,
      filterJson:   buildFilterJson(),
      isActive:     form.isActive ? 1 : 0,
    };
    try {
      if (isEdit) {
        await adminCollections.update(collection.collectionID, payload);
        onSaved('updated');
      } else {
        await adminCollections.create(payload);
        onSaved('created');
      }
      onClose();
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to save collection.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminCollections.remove(collection.collectionID);
      onSaved('deleted');
      onClose();
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to delete collection.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const coverPreview = imgSrc(form.coverImageUrl);

  return (
    <>
      <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)', zIndex: 1060 }}>
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{isEdit ? 'Edit Collection' : 'Add Collection'}</h5>
              <button className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body">
              {/* Name */}
              <div className="mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  maxLength={100}
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="e.g. Summer Whites"
                />
              </div>

              {/* Description */}
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={2}
                  maxLength={300}
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Short description shown on the storefront"
                />
                <div className="form-text">{form.description.length}/300</div>
              </div>

              {/* Cover image */}
              <div className="mb-3">
                <label className="form-label">Cover Image URL</label>
                <div className="d-flex gap-2">
                  <input
                    className="form-control form-control-sm"
                    value={form.coverImageUrl}
                    onChange={e => set('coverImageUrl', e.target.value)}
                    placeholder="https://… or /uploads/collections/…"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm flex-shrink-0 d-flex align-items-center gap-1"
                    disabled={imgUploading}
                    onClick={() => coverFileRef.current?.click()}
                  >
                    {imgUploading
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><ImageIcon size={13} /> Upload</>
                    }
                  </button>
                  <input
                    ref={coverFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="d-none"
                    onChange={e => { handleCoverUpload(e.target.files[0]); e.target.value = ''; }}
                  />
                </div>
                {coverPreview && (
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="mt-2 rounded"
                    style={{ maxHeight: 80, objectFit: 'cover', maxWidth: '100%' }}
                  />
                )}
              </div>

              <hr className="my-3" />
              <p className="small fw-semibold text-muted mb-3">Product Filters (optional)</p>

              {/* Wine type */}
              <div className="mb-3">
                <label className="form-label small">Wine Type</label>
                <select
                  className="form-select form-select-sm"
                  value={form.wineType}
                  onChange={e => set('wineType', e.target.value)}
                >
                  <option value="">Any</option>
                  {WINE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* Varietal / Vintage / Max Price */}
              <div className="row g-2 mb-3">
                <div className="col">
                  <label className="form-label small">Varietal</label>
                  <input
                    className="form-control form-control-sm"
                    value={form.varietal}
                    onChange={e => set('varietal', e.target.value)}
                    placeholder="e.g. Pinot Noir"
                  />
                </div>
                <div className="col">
                  <label className="form-label small">Vintage</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={form.vintage}
                    onChange={e => set('vintage', e.target.value)}
                    placeholder="e.g. 2021"
                    min={1900}
                    max={2100}
                  />
                </div>
                <div className="col">
                  <label className="form-label small">Max Price / case</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      value={form.maxPrice}
                      onChange={e => set('maxPrice', e.target.value)}
                      placeholder="—"
                      min={0}
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Active toggle */}
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="collModalActive"
                  checked={form.isActive}
                  onChange={e => set('isActive', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="collModalActive">Active</label>
              </div>
            </div>

            <div className="modal-footer">
              {isEdit && (
                <button
                  className="btn btn-outline-danger me-auto"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting || saving}
                >
                  {deleting ? <span className="spinner-border spinner-border-sm" /> : 'Delete'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary d-inline-flex align-items-center gap-1"
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
              >
                {saving && <span className="spinner-border spinner-border-sm" />}
                {isEdit ? 'Save Changes' : 'Create Collection'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        title="Delete Collection"
        body={`Delete "${form.name}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  );
}

// ── Tab 5: Storefront ──────────────────────────────────────────────────────────

function StorefrontTab({ toast, settings, onSettingsChange }) {
  const [saving,              setSaving]              = useState(false);
  const [collections,         setCollections]         = useState([]);
  const [loadingCollections,  setLoadingCollections]  = useState(true);
  const [collectionModal,     setCollectionModal]     = useState(null); // null | 'new' | collection obj
  const [bannerUploading,     setBannerUploading]     = useState(false);
  const [logoUploading,       setLogoUploading]       = useState(false);
  const bannerFileRef = useRef(null);
  const logoFileRef   = useRef(null);

  // ── Load collections ────────────────────────────────────────────────────────
  const loadCollections = useCallback(() => {
    setLoadingCollections(true);
    adminCollections.list()
      .then(r => setCollections(r.data.data || []))
      .catch(() => toast('Failed to load collections.', 'error'))
      .finally(() => setLoadingCollections(false));
  }, [toast]);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  // ── Banner image upload ─────────────────────────────────────────────────────
  const handleBannerUpload = async (file) => {
    if (!file) return;
    setBannerUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await adminUploads.uploadBannerImage(fd);
      if (res.data?.success) {
        onSettingsChange('storefront_banner_image_url', res.data.url);
        toast('Banner image uploaded.');
      }
    } catch {
      toast('Failed to upload banner image.', 'error');
    } finally {
      setBannerUploading(false);
    }
  };

  // ── Logo image upload ───────────────────────────────────────────────────────
  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await adminUploads.uploadLogoImage(fd);
      if (res.data?.success) {
        onSettingsChange('invoice_logo_url', res.data.data.imageUrl);
        toast('Invoice logo uploaded.');
      }
    } catch {
      toast('Failed to upload logo.', 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoClear = async () => {
    try {
      await adminUploads.deleteLogoImage();
      onSettingsChange('invoice_logo_url', '');
      toast('Invoice logo removed.');
    } catch {
      toast('Failed to remove logo.', 'error');
    }
  };

  // ── Save storefront settings ────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSettings.updateSystem({
        storefront_banner_image_url:   settings.storefront_banner_image_url   || '',
        storefront_banner_headline:    settings.storefront_banner_headline    || '',
        storefront_banner_subheadline: settings.storefront_banner_subheadline || '',
        storefront_banner_cta_label:   settings.storefront_banner_cta_label   || '',
        storefront_banner_cta_url:     settings.storefront_banner_cta_url     || '',
        storefront_show_collections:   settings.storefront_show_collections   || '1',
        invoice_logo_url:              settings.invoice_logo_url              || '',
        storefront_video_url:          settings.storefront_video_url          || '',
        storefront_video_headline:     settings.storefront_video_headline     || '',
        storefront_video_body:         settings.storefront_video_body         || '',
      });
      toast('Storefront settings saved.');
    } catch {
      toast('Failed to save settings.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const bannerPreview     = imgSrc(settings.storefront_banner_image_url);
  const showCollections   = settings.storefront_show_collections !== '0';

  // Build a human-readable filter summary for the collections table
  const filterSummary = (filterJson) => {
    if (!filterJson) return '—';
    try {
      const f = JSON.parse(filterJson);
      const parts = [];
      if (f.wineType) parts.push(f.wineType);
      if (f.varietal) parts.push(f.varietal);
      if (f.vintage)  parts.push(String(f.vintage));
      if (f.maxPrice) parts.push(`≤$${f.maxPrice}`);
      return parts.join(' · ') || '—';
    } catch {
      return '—';
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <h6 className="fw-semibold mb-3">Storefront Settings</h6>

      {/* ── Card 1: Hero Banner ── */}
      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Hero Banner</div>
        <div className="card-body">

          {/* Banner image preview + URL input + upload button */}
          <div className="mb-3">
            <label className="form-label">Banner Image</label>
            {bannerPreview ? (
              <img
                src={bannerPreview}
                alt="Banner preview"
                className="d-block w-100 rounded mb-2"
                style={{ maxHeight: 200, objectFit: 'cover' }}
              />
            ) : (
              <div
                className="w-100 rounded mb-2 bg-light d-flex align-items-center justify-content-center border"
                style={{ height: 100 }}
              >
                <span className="text-muted small">No banner image set</span>
              </div>
            )}
            <div className="d-flex gap-2">
              <input
                className="form-control form-control-sm"
                value={settings.storefront_banner_image_url || ''}
                onChange={e => onSettingsChange('storefront_banner_image_url', e.target.value)}
                placeholder="https://… or /uploads/banners/…"
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm flex-shrink-0 d-flex align-items-center gap-1"
                disabled={bannerUploading}
                onClick={() => bannerFileRef.current?.click()}
              >
                {bannerUploading
                  ? <span className="spinner-border spinner-border-sm" />
                  : <><ImageIcon size={13} /> Upload Image</>
                }
              </button>
              <input
                ref={bannerFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="d-none"
                onChange={e => { handleBannerUpload(e.target.files[0]); e.target.value = ''; }}
              />
            </div>
          </div>

          {/* Headline */}
          <div className="mb-3">
            <label className="form-label">Headline</label>
            <input
              className="form-control"
              maxLength={80}
              value={settings.storefront_banner_headline || ''}
              onChange={e => onSettingsChange('storefront_banner_headline', e.target.value)}
              placeholder="Welcome to Jeremy Wine Co."
            />
            <div className="form-text">{(settings.storefront_banner_headline || '').length}/80</div>
          </div>

          {/* Subheadline */}
          <div className="mb-3">
            <label className="form-label">Subheadline</label>
            <input
              className="form-control"
              maxLength={160}
              value={settings.storefront_banner_subheadline || ''}
              onChange={e => onSettingsChange('storefront_banner_subheadline', e.target.value)}
              placeholder="Discover our portfolio of estate wines."
            />
            <div className="form-text">{(settings.storefront_banner_subheadline || '').length}/160</div>
          </div>

          {/* CTA label + URL */}
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">CTA Button Label</label>
              <input
                className="form-control"
                maxLength={40}
                value={settings.storefront_banner_cta_label || ''}
                onChange={e => onSettingsChange('storefront_banner_cta_label', e.target.value)}
                placeholder="Shop Now"
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">CTA Destination URL</label>
              <input
                className="form-control"
                value={settings.storefront_banner_cta_url || ''}
                onChange={e => onSettingsChange('storefront_banner_cta_url', e.target.value)}
                placeholder="/wholesale/catalog"
              />
              <div className="form-text">
                Use <code>/wholesale/catalog</code> to link to the full catalog, or{' '}
                <code>/wholesale/catalog?wineType=Red</code> to link to a filtered view.
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Card 2: Collections Row ── */}
      <div className="card mb-4">
        <div className="card-header bg-light d-flex align-items-center justify-content-between">
          <span className="fw-semibold">Collections Row</span>
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => setCollectionModal('new')}
          >
            <Plus size={13} /> Add Collection
          </button>
        </div>
        <div className="card-body">

          {/* Show/hide toggle */}
          <div className="form-check form-switch mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              id="showCollectionsSwitch"
              checked={showCollections}
              onChange={e => onSettingsChange('storefront_show_collections', e.target.checked ? '1' : '0')}
            />
            <label className="form-check-label" htmlFor="showCollectionsSwitch">
              Show Collections Row on storefront home
            </label>
          </div>

          {/* Collections table */}
          {loadingCollections ? (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm text-secondary" />
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center text-muted py-3 small">
              No collections yet. Click "Add Collection" to create one.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Filter Summary</th>
                    <th>Active</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map(col => (
                    <tr key={col.collectionID}>
                      <td className="fw-semibold">{col.name}</td>
                      <td className="text-muted small">{filterSummary(col.filterJson)}</td>
                      <td>
                        <span className={`badge ${col.isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {col.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                          onClick={() => setCollectionModal(col)}
                        >
                          <Edit2 size={12} /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Card 3: Invoice Logo ── */}
      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Invoice Logo</div>
        <div className="card-body">
          <p className="text-muted small mb-3">
            Square logo printed in the top-left corner of PDF invoices. Recommended size: 200×200 px. Max 2 MB.
          </p>
          <div className="d-flex align-items-start gap-3">
            {/* Preview */}
            {settings.invoice_logo_url ? (
              <img
                src={
                  settings.invoice_logo_url.startsWith('http')
                    ? settings.invoice_logo_url
                    : `${process.env.REACT_APP_API_URL || ''}${settings.invoice_logo_url}`
                }
                alt="Invoice logo preview"
                className="rounded border"
                style={{ width: 80, height: 80, objectFit: 'contain', background: '#f8f8f8' }}
              />
            ) : (
              <div
                className="rounded border bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 80, height: 80 }}
              >
                <span className="text-muted" style={{ fontSize: 11 }}>No logo</span>
              </div>
            )}

            {/* URL + Upload + Clear */}
            <div className="flex-grow-1">
              <div className="d-flex gap-2 mb-2">
                <input
                  className="form-control form-control-sm"
                  value={settings.invoice_logo_url || ''}
                  onChange={e => onSettingsChange('invoice_logo_url', e.target.value)}
                  placeholder="https://… or /uploads/logo/…"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm flex-shrink-0 d-flex align-items-center gap-1"
                  disabled={logoUploading}
                  onClick={() => logoFileRef.current?.click()}
                >
                  {logoUploading
                    ? <span className="spinner-border spinner-border-sm" />
                    : <><ImageIcon size={13} /> Upload</>
                  }
                </button>
                <input
                  ref={logoFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="d-none"
                  onChange={e => { handleLogoUpload(e.target.files[0]); e.target.value = ''; }}
                />
              </div>
              {settings.invoice_logo_url && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleLogoClear}
                >
                  Remove Logo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Card 4: Winemaker Video Section ── */}
      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">Winemaker Video Section</div>
        <div className="card-body">

          <div className="mb-3">
            <label className="form-label">Winemaker Video URL</label>
            <input
              className="form-control"
              value={settings.storefront_video_url || ''}
              onChange={e => onSettingsChange('storefront_video_url', e.target.value)}
              placeholder="https://www.youtube.com/embed/VIDEO_ID"
            />
            <div className="form-text">
              Paste a YouTube embed URL to show the video section on the homepage. Leave blank to hide the section.
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Video Section Headline</label>
            <input
              className="form-control"
              value={settings.storefront_video_headline || ''}
              onChange={e => onSettingsChange('storefront_video_headline', e.target.value)}
              placeholder="Lodi-grown. Handcrafted. Yours to pour."
            />
            <div className="form-text">
              Heading displayed next to the video. Defaults to placeholder text if blank.
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Video Section Description</label>
            <textarea
              className="form-control"
              rows={3}
              value={settings.storefront_video_body || ''}
              onChange={e => onSettingsChange('storefront_video_body', e.target.value)}
              placeholder="Learn about the stories behind our estate and partner vineyards..."
            />
            <div className="form-text">
              Paragraph text displayed next to the video.
            </div>
          </div>

          <p className="text-muted small mt-3">
            <strong>Featured Products</strong> are managed from the{' '}
            <a href="/admin/products">Products</a> page using the Featured flag and sort order on each product.
          </p>

        </div>
      </div>

      {/* Save button */}
      <button
        className="btn btn-primary d-inline-flex align-items-center gap-1"
        onClick={handleSave}
        disabled={saving}
      >
        {saving && <span className="spinner-border spinner-border-sm" />}
        Save Storefront Settings
      </button>

      {/* Collection modal */}
      {collectionModal && (
        <CollectionModal
          collection={collectionModal === 'new' ? null : collectionModal}
          toast={toast}
          onSaved={(action) => {
            loadCollections();
            if (action === 'created') toast('Collection created.');
            else if (action === 'updated') toast('Collection updated.');
            else if (action === 'deleted') toast('Collection deleted.');
          }}
          onClose={() => setCollectionModal(null)}
        />
      )}
    </>
  );
}

// ── Tab 6: Shipping ────────────────────────────────────────────────────────────

function ShippingTab({ toast, settings, onSettingsChange }) {
  const [saving,       setSaving]       = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSettings.updateSystem({
        gls_username:       settings.gls_username       || '',
        gls_password:       settings.gls_password       || '',
        gls_account_number: settings.gls_account_number || '',
        gls_label_email:    settings.gls_label_email    || '',
        gls_pickup_phone:   settings.gls_pickup_phone   || '',
      });
      toast('Shipping settings saved.');
    } catch {
      toast('Failed to save settings.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h6 className="fw-semibold mb-3">Shipping Settings</h6>

      <div className="card mb-4">
        <div className="card-header bg-light fw-semibold">GLS ShipmentHub Credentials</div>
        <div className="card-body row g-3">
          <div className="col-md-6">
            <label className="form-label">GLS Username</label>
            <input
              type="text"
              className="form-control"
              value={settings.gls_username || ''}
              onChange={e => onSettingsChange('gls_username', e.target.value)}
              placeholder="GLS account username"
              autoComplete="off"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">GLS Password</label>
            <div className="input-group">
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-control"
                value={settings.gls_password || ''}
                onChange={e => onSettingsChange('gls_password', e.target.value)}
                placeholder="GLS account password"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">GLS Account Number</label>
            <input
              type="text"
              className="form-control"
              value={settings.gls_account_number || ''}
              onChange={e => onSettingsChange('gls_account_number', e.target.value)}
              placeholder="GLS account number"
              autoComplete="off"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Label Recipient Email</label>
            <input
              type="email"
              className="form-control"
              value={settings.gls_label_email || ''}
              onChange={e => onSettingsChange('gls_label_email', e.target.value)}
              placeholder="warehouse@example.com"
            />
            <div className="form-text">
              Email address where shipping labels are sent (e.g. warehouse or fulfillment team)
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Pickup Contact Phone</label>
            <input
              type="tel"
              className="form-control"
              value={settings.gls_pickup_phone || ''}
              onChange={e => onSettingsChange('gls_pickup_phone', e.target.value)}
              placeholder="+1 (555) 000-0000"
            />
            <div className="form-text">
              Phone number included in GLS on-call pickup requests
            </div>
          </div>

          <div className="col-12">
            <p className="text-muted small mb-0">
              Credentials are provided by GLS US. Contact your GLS account rep to obtain them.
            </p>
          </div>
        </div>
      </div>

      <button
        className="btn btn-primary d-inline-flex align-items-center gap-1"
        onClick={handleSave}
        disabled={saving}
      >
        {saving && <span className="spinner-border spinner-border-sm" />}
        Save Shipping Settings
      </button>
    </>
  );
}

// ── Tab 7: Fulfillment (Pickup Locations + Shipping Rules) ────────────────────

// Build a human-readable summary of a shipping rule
function ruleDescription(rule) {
  const condLabel = {
    case_count:   'order is',
    order_value:  'order total is',
    customer_tag: 'customer tag is',
  }[rule.condition_type] || rule.condition_type;

  const opLabel = {
    gte: (val, type) => type === 'order_value' ? `$${parseFloat(val).toFixed(2)} or more` : `${val} or more cases`,
    lte: (val, type) => type === 'order_value' ? `$${parseFloat(val).toFixed(2)} or less`  : `${val} or fewer cases`,
    eq:  (val, type) => type === 'order_value' ? `exactly $${parseFloat(val).toFixed(2)}`    : `exactly ${val} cases`,
  }[rule.operator];

  const condStr = opLabel
    ? `${condLabel} ${opLabel(parseFloat(rule.threshold_value), rule.condition_type)}`
    : `${condLabel} ${rule.operator} ${rule.threshold_value}`;

  const resultStr = {
    free:                 'Free shipping',
    flat_rate:            `Flat rate $${parseFloat(rule.result_value).toFixed(2)}`,
    percentage_discount:  `${parseFloat(rule.result_value).toFixed(0)}% shipping discount`,
  }[rule.result_type] || rule.result_type;

  return `${resultStr} when ${condStr}`;
}

const EMPTY_LOCATION = { name: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', pickup_instructions: '', lead_time_days: 2, is_active: true };
const EMPTY_RULE     = { rule_name: '', priority: 10, condition_type: 'case_count', operator: 'gte', threshold_value: '', result_type: 'flat_rate', result_value: '', is_active: true };

function FulfillmentTab({ toast }) {
  // ── Pickup Locations ────────────────────────────────────────────────────────
  const [locations,    setLocations]    = useState([]);
  const [locLoading,   setLocLoading]   = useState(true);
  const [locModal,     setLocModal]     = useState(null);   // null | 'new' | row
  const [locForm,      setLocForm]      = useState({ ...EMPTY_LOCATION });
  const [locSaving,    setLocSaving]    = useState(false);
  const [locConfirm,   setLocConfirm]   = useState(null);  // id to deactivate

  // ── Shipping Rules ───────────────────────────────────────────────────────────
  const [rules,        setRules]        = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [ruleModal,    setRuleModal]    = useState(null);  // null | 'new' | row
  const [ruleForm,     setRuleForm]     = useState({ ...EMPTY_RULE });
  const [ruleSaving,   setRuleSaving]   = useState(false);
  const [ruleConfirm,  setRuleConfirm]  = useState(null); // id to deactivate

  // ── Load data ────────────────────────────────────────────────────────────────
  const loadLocations = useCallback(() => {
    setLocLoading(true);
    adminPickupLocations.list()
      .then(r => setLocations(r.data.data || []))
      .catch(() => toast('Failed to load pickup locations.', 'error'))
      .finally(() => setLocLoading(false));
  }, [toast]);

  const loadRules = useCallback(() => {
    setRulesLoading(true);
    adminShippingRules.list()
      .then(r => setRules(r.data.data || []))
      .catch(() => toast('Failed to load shipping rules.', 'error'))
      .finally(() => setRulesLoading(false));
  }, [toast]);

  useEffect(() => { loadLocations(); loadRules(); }, [loadLocations, loadRules]);

  // ── Pickup Locations: helpers ────────────────────────────────────────────────
  const openLocModal = (row) => {
    if (row === 'new') {
      setLocForm({ ...EMPTY_LOCATION });
    } else {
      setLocForm({
        name:                row.name,
        address_line1:       row.address_line1,
        address_line2:       row.address_line2 || '',
        city:                row.city,
        state:               row.state,
        zip:                 row.zip,
        pickup_instructions: row.pickup_instructions || '',
        lead_time_days:      row.lead_time_days,
        is_active:           !!row.is_active,
      });
    }
    setLocModal(row);
  };

  const saveLocation = async () => {
    setLocSaving(true);
    const payload = {
      name:                locForm.name.trim(),
      address_line1:       locForm.address_line1.trim(),
      address_line2:       locForm.address_line2.trim() || null,
      city:                locForm.city.trim(),
      state:               locForm.state.trim().toUpperCase(),
      zip:                 locForm.zip.trim(),
      pickup_instructions: locForm.pickup_instructions.trim() || null,
      lead_time_days:      parseInt(locForm.lead_time_days) || 2,
      is_active:           locForm.is_active ? 1 : 0,
    };
    try {
      if (locModal === 'new') {
        await adminPickupLocations.create(payload);
        toast('Pickup location created.');
      } else {
        await adminPickupLocations.update(locModal.id, payload);
        toast('Pickup location updated.');
      }
      setLocModal(null);
      loadLocations();
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to save pickup location.', 'error');
    } finally {
      setLocSaving(false);
    }
  };

  const deactivateLocation = async () => {
    const id = locConfirm;
    setLocConfirm(null);
    try {
      await adminPickupLocations.remove(id);
      toast('Pickup location deactivated.');
      loadLocations();
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to deactivate location.', 'error');
    }
  };

  const toggleLocActive = async (loc) => {
    try {
      await adminPickupLocations.update(loc.id, { is_active: loc.is_active ? 0 : 1 });
      toast(loc.is_active ? 'Location deactivated.' : 'Location activated.');
      loadLocations();
    } catch (e) {
      toast('Failed to toggle location.', 'error');
    }
  };

  // ── Shipping Rules: helpers ──────────────────────────────────────────────────
  const openRuleModal = (row) => {
    if (row === 'new') {
      setRuleForm({ ...EMPTY_RULE });
    } else {
      setRuleForm({
        rule_name:       row.rule_name,
        priority:        row.priority,
        condition_type:  row.condition_type,
        operator:        row.operator,
        threshold_value: parseFloat(row.threshold_value),
        result_type:     row.result_type,
        result_value:    parseFloat(row.result_value),
        is_active:       !!row.is_active,
      });
    }
    setRuleModal(row);
  };

  const saveRule = async () => {
    setRuleSaving(true);
    const payload = {
      rule_name:       ruleForm.rule_name.trim(),
      priority:        parseInt(ruleForm.priority) || 0,
      condition_type:  ruleForm.condition_type,
      operator:        ruleForm.operator,
      threshold_value: parseFloat(ruleForm.threshold_value),
      result_type:     ruleForm.result_type,
      result_value:    parseFloat(ruleForm.result_value) || 0,
      is_active:       ruleForm.is_active ? 1 : 0,
    };
    try {
      if (ruleModal === 'new') {
        await adminShippingRules.create(payload);
        toast('Shipping rule created.');
      } else {
        await adminShippingRules.update(ruleModal.id, payload);
        toast('Shipping rule updated.');
      }
      setRuleModal(null);
      loadRules();
    } catch (e) {
      toast(e.response?.data?.message || 'Failed to save shipping rule.', 'error');
    } finally {
      setRuleSaving(false);
    }
  };

  const toggleRuleActive = async (rule) => {
    try {
      await adminShippingRules.update(rule.id, { is_active: rule.is_active ? 0 : 1 });
      toast(rule.is_active ? 'Rule deactivated.' : 'Rule activated.');
      loadRules();
    } catch (e) {
      toast('Failed to toggle rule.', 'error');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Pickup Locations ─────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
          <MapPin size={16} /> Pickup Locations
        </h6>
        <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={() => openLocModal('new')}>
          <Plus size={14} /> Add Location
        </button>
      </div>

      {locLoading ? (
        <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
      ) : locations.length === 0 ? (
        <div className="text-center text-muted py-4">
          <MapPin size={32} className="mb-2 opacity-50" />
          <p className="mb-0">No pickup locations configured.</p>
        </div>
      ) : (
        <div className="table-responsive mb-2">
          <table className="table table-hover table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th className="text-center">Lead Time</th>
                <th className="text-center">Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {locations.map(loc => (
                <tr key={loc.id}>
                  <td className="fw-semibold">{loc.name}</td>
                  <td className="small text-muted">
                    {loc.address_line1}{loc.address_line2 ? `, ${loc.address_line2}` : ''}<br />
                    {loc.city}, {loc.state} {loc.zip}
                  </td>
                  <td className="text-center small">{loc.lead_time_days} day{loc.lead_time_days !== 1 ? 's' : ''}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-link p-0 border-0"
                      title={loc.is_active ? 'Deactivate' : 'Activate'}
                      onClick={() => toggleLocActive(loc)}
                    >
                      {loc.is_active
                        ? <ToggleRight size={22} className="text-success" />
                        : <ToggleLeft  size={22} className="text-muted" />}
                    </button>
                  </td>
                  <td>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => openLocModal(loc)}>
                      <Edit2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-muted small mb-5">
        <AlertCircle size={13} className="me-1" />
        Deactivated locations are hidden from customers but not deleted. Update placeholder addresses before going live.
      </p>

      <hr className="mb-4" />

      {/* ── Shipping Rules ────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
          <Package size={16} /> Shipping Rules
        </h6>
        <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={() => openRuleModal('new')}>
          <Plus size={14} /> Add Rule
        </button>
      </div>

      <p className="text-muted small mb-3">
        Rules are evaluated in <strong>priority order</strong> (lowest number first). The first matching rule wins.
      </p>

      {rulesLoading ? (
        <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
      ) : rules.length === 0 ? (
        <div className="text-center text-muted py-4">
          <Package size={32} className="mb-2 opacity-50" />
          <p className="mb-0">No shipping rules configured. All orders will have $0 shipping.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-center" style={{ width: 60 }}>Priority</th>
                <th>Rule</th>
                <th>Summary</th>
                <th className="text-center">Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rules.map(rule => (
                <tr key={rule.id} className={!rule.is_active ? 'text-muted' : ''}>
                  <td className="text-center fw-semibold">{rule.priority}</td>
                  <td className="fw-semibold">{rule.rule_name}</td>
                  <td className="small text-muted" style={{ maxWidth: 300 }}>{ruleDescription(rule)}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-link p-0 border-0"
                      title={rule.is_active ? 'Deactivate' : 'Activate'}
                      onClick={() => toggleRuleActive(rule)}
                    >
                      {rule.is_active
                        ? <ToggleRight size={22} className="text-success" />
                        : <ToggleLeft  size={22} className="text-muted" />}
                    </button>
                  </td>
                  <td>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => openRuleModal(rule)}>
                      <Edit2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-muted small mt-2">
        <AlertCircle size={13} className="me-1" />
        Deactivated rules are skipped. Review placeholder flat-rate amount before going live.
      </p>

      {/* ── Pickup Location Modal ─────────────────────────────────────────── */}
      {locModal !== null && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{locModal === 'new' ? 'Add Pickup Location' : 'Edit Pickup Location'}</h5>
                <button className="btn-close" onClick={() => setLocModal(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Name <span className="text-danger">*</span></label>
                  <input className="form-control" value={locForm.name}
                    onChange={e => setLocForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Healdsburg Tasting Room" />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Address Line 1 <span className="text-danger">*</span></label>
                  <input className="form-control" value={locForm.address_line1}
                    onChange={e => setLocForm(f => ({ ...f, address_line1: e.target.value }))}
                    placeholder="123 Main Street" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Address Line 2</label>
                  <input className="form-control" value={locForm.address_line2}
                    onChange={e => setLocForm(f => ({ ...f, address_line2: e.target.value }))}
                    placeholder="Suite, Unit, Building (optional)" />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">City <span className="text-danger">*</span></label>
                    <input className="form-control" value={locForm.city}
                      onChange={e => setLocForm(f => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div className="col-3">
                    <label className="form-label fw-semibold">State <span className="text-danger">*</span></label>
                    <input className="form-control" value={locForm.state} maxLength={2}
                      onChange={e => setLocForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} />
                  </div>
                  <div className="col-3">
                    <label className="form-label fw-semibold">ZIP <span className="text-danger">*</span></label>
                    <input className="form-control" value={locForm.zip}
                      onChange={e => setLocForm(f => ({ ...f, zip: e.target.value }))} />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Pickup Instructions</label>
                  <textarea className="form-control" rows={3} value={locForm.pickup_instructions}
                    onChange={e => setLocForm(f => ({ ...f, pickup_instructions: e.target.value }))}
                    placeholder="Instructions shown to the customer at checkout and on their order confirmation." />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Lead Time (business days)</label>
                  <input type="number" className="form-control" min={0} max={30}
                    value={locForm.lead_time_days}
                    onChange={e => setLocForm(f => ({ ...f, lead_time_days: e.target.value }))} />
                  <div className="form-text">Shown to customers as estimated pickup readiness.</div>
                </div>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="locActive"
                    checked={locForm.is_active}
                    onChange={e => setLocForm(f => ({ ...f, is_active: e.target.checked }))} />
                  <label className="form-check-label" htmlFor="locActive">Active (visible to customers)</label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setLocModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveLocation} disabled={locSaving || !locForm.name.trim() || !locForm.address_line1.trim()}>
                  {locSaving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                  {locModal === 'new' ? 'Create' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Pickup Location Deactivate Confirm ───────────────────────────── */}
      <ConfirmModal
        show={locConfirm !== null}
        title="Deactivate Location"
        body="This will hide the location from customers. Existing orders are not affected."
        onConfirm={deactivateLocation}
        onCancel={() => setLocConfirm(null)}
      />

      {/* ── Shipping Rule Modal ───────────────────────────────────────────── */}
      {ruleModal !== null && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{ruleModal === 'new' ? 'Add Shipping Rule' : 'Edit Shipping Rule'}</h5>
                <button className="btn-close" onClick={() => setRuleModal(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Rule Name <span className="text-danger">*</span></label>
                  <input className="form-control" value={ruleForm.rule_name}
                    onChange={e => setRuleForm(f => ({ ...f, rule_name: e.target.value }))}
                    placeholder="e.g. Free Shipping — 3+ Cases" />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Priority</label>
                  <input type="number" className="form-control" min={0} value={ruleForm.priority}
                    onChange={e => setRuleForm(f => ({ ...f, priority: e.target.value }))} />
                  <div className="form-text">Lower number = evaluated first. First matching rule wins.</div>
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-4">
                    <label className="form-label fw-semibold">Condition</label>
                    <select className="form-select" value={ruleForm.condition_type}
                      onChange={e => setRuleForm(f => ({ ...f, condition_type: e.target.value }))}>
                      <option value="case_count">Case Count</option>
                      <option value="order_value">Order Value ($)</option>
                    </select>
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-semibold">Operator</label>
                    <select className="form-select" value={ruleForm.operator}
                      onChange={e => setRuleForm(f => ({ ...f, operator: e.target.value }))}>
                      <option value="gte">≥ (at least)</option>
                      <option value="lte">≤ (at most)</option>
                      <option value="eq">= (exactly)</option>
                    </select>
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-semibold">
                      {ruleForm.condition_type === 'order_value' ? 'Amount ($)' : 'Cases'}
                      <span className="text-danger"> *</span>
                    </label>
                    <input type="number" className="form-control" min={0} step={ruleForm.condition_type === 'order_value' ? '0.01' : '1'}
                      value={ruleForm.threshold_value}
                      onChange={e => setRuleForm(f => ({ ...f, threshold_value: e.target.value }))} />
                  </div>
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Result</label>
                    <select className="form-select" value={ruleForm.result_type}
                      onChange={e => setRuleForm(f => ({ ...f, result_type: e.target.value }))}>
                      <option value="free">Free Shipping</option>
                      <option value="flat_rate">Flat Rate ($)</option>
                      <option value="percentage_discount">Percentage Discount</option>
                    </select>
                  </div>
                  {ruleForm.result_type !== 'free' && (
                    <div className="col-6">
                      <label className="form-label fw-semibold">
                        {ruleForm.result_type === 'percentage_discount' ? 'Discount %' : 'Rate ($)'}
                        <span className="text-danger"> *</span>
                      </label>
                      <input type="number" className="form-control" min={0} step="0.01"
                        value={ruleForm.result_value}
                        onChange={e => setRuleForm(f => ({ ...f, result_value: e.target.value }))} />
                    </div>
                  )}
                </div>
                {ruleForm.rule_name && ruleForm.threshold_value !== '' && (
                  <div className="alert alert-light border small">
                    <strong>Preview:</strong>{' '}
                    {ruleDescription({
                      condition_type: ruleForm.condition_type,
                      operator: ruleForm.operator,
                      threshold_value: ruleForm.threshold_value,
                      result_type: ruleForm.result_type,
                      result_value: ruleForm.result_value || 0,
                    })}
                  </div>
                )}
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="ruleActive"
                    checked={ruleForm.is_active}
                    onChange={e => setRuleForm(f => ({ ...f, is_active: e.target.checked }))} />
                  <label className="form-check-label" htmlFor="ruleActive">Active</label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setRuleModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveRule}
                  disabled={ruleSaving || !ruleForm.rule_name.trim() || ruleForm.threshold_value === ''}>
                  {ruleSaving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                  {ruleModal === 'new' ? 'Create' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Shipping Rule Deactivate Confirm ─────────────────────────────── */}
      <ConfirmModal
        show={ruleConfirm !== null}
        title="Deactivate Rule"
        body="This rule will be skipped when calculating shipping. Existing orders are not affected."
        onConfirm={() => { const id = ruleConfirm; setRuleConfirm(null); adminShippingRules.remove(id).then(loadRules).catch(() => toast('Failed.', 'error')); }}
        onCancel={() => setRuleConfirm(null)}
      />
    </>
  );
}

// ── Main Settings Page ─────────────────────────────────────────────────────────

const TABS = [
  { id: 'users',       label: 'Users & Access',          icon: Users },
  { id: 'reps',        label: 'Sales Reps',               icon: UserCheck },
  { id: 'email',       label: 'Email & Notifications',    icon: Mail },
  { id: 'portal',      label: 'Portal Settings',          icon: Settings2 },
  { id: 'storefront',  label: 'Storefront',               icon: ShoppingBag },
  { id: 'shipping',    label: 'Shipping',                 icon: Truck },
  { id: 'fulfillment', label: 'Fulfillment',              icon: MapPin },
];

export default function Settings() {
  const [activeTab, setActiveTab]   = useState('users');
  const [sysSettings, setSysSettings] = useState({});
  const [sysLoading, setSysLoading] = useState(true);
  const { toasts, add: toast, remove } = useToast();

  useEffect(() => {
    adminSettings.getSystem()
      .then(r => setSysSettings(r.data.settings || {}))
      .catch(() => toast('Failed to load system settings.', 'error'))
      .finally(() => setSysLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSettingsChange = (key, value) => {
    setSysSettings(s => ({ ...s, [key]: value }));
  };

  return (
    <div>
      <ToastContainer toasts={toasts} remove={remove} />

      {/* Page header */}
      <div className="py-4 px-4 bg-white border-bottom d-flex align-items-center gap-2">
        <Settings2 size={20} />
        <h5 className="mb-0 fw-bold">Settings</h5>
      </div>

      <div className="container-fluid py-4 px-4">
        <div className="row g-4">
          {/* Left tab nav */}
          <div className="col-12 col-md-3">
            <div className="nav flex-column nav-pills">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  className={`nav-link text-start d-flex align-items-center gap-2 mb-1 ${activeTab === id ? 'active' : 'text-dark'}`}
                  onClick={() => setActiveTab(id)}
                >
                  <Icon size={15} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="col-12 col-md-9">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                {activeTab === 'users' && (
                  <UsersTab toast={toast} />
                )}
                {activeTab === 'reps' && (
                  <RepsTab toast={toast} />
                )}
                {activeTab === 'email' && (
                  sysLoading
                    ? <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
                    : <EmailTab toast={toast} settings={sysSettings} onSettingsChange={handleSettingsChange} />
                )}
                {activeTab === 'portal' && (
                  sysLoading
                    ? <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
                    : <PortalTab toast={toast} settings={sysSettings} onSettingsChange={handleSettingsChange} />
                )}
                {activeTab === 'storefront' && (
                  sysLoading
                    ? <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
                    : <StorefrontTab toast={toast} settings={sysSettings} onSettingsChange={handleSettingsChange} />
                )}
                {activeTab === 'shipping' && (
                  sysLoading
                    ? <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
                    : <ShippingTab toast={toast} settings={sysSettings} onSettingsChange={handleSettingsChange} />
                )}
                {activeTab === 'fulfillment' && (
                  <FulfillmentTab toast={toast} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
